// src/components/kiosk/POSSection.jsx
import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext.jsx';

// Import sub-components
import POSHeader from './POSSection/POSHeader';
import SectionSelector from './POSSection/SectionSelector';
import CategoryGrid from './POSSection/CategoryGrid';
import ItemsGrid from './POSSection/ItemsGrid';
import Cart from './POSSection/Cart';
import CustomerModal from './POSSection/CustomerModal';
import PaymentModal from './POSSection/PaymentModal';
import OrdersModal from './POSSection/OrdersModal';
import ReceiptPrinter from './POSSection/ReceiptPrinterBW'

function POSSection() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [heldCarts, setHeldCarts] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  
  const [search, setSearch] = useState('');
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const [customerDetails, setCustomerDetails] = useState({ 
    name: '', 
    phone: '', 
    email: '', 
    dateOfBirth: '' 
  });
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [applyVAT, setApplyVAT] = useState(false);
  
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const VAT_RATE = 0.075;

  // ✅ CRITICAL FIX: Helper to get correct token based on context
  const getAuthToken = () => {
    // Kiosk context - use kioskAuthToken
    return localStorage.getItem('kioskAuthToken') || localStorage.getItem('token'); // fallback to legacy
  };

  // Load held carts from sessionStorage on mount
  useEffect(() => {
    const savedHeldCarts = sessionStorage.getItem('heldCarts');
    if (savedHeldCarts) {
      try {
        const parsed = JSON.parse(savedHeldCarts);
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        const validCarts = parsed.filter(hc => hc.timestamp > oneHourAgo);
        setHeldCarts(validCarts);
        if (validCarts.length < parsed.length) {
          sessionStorage.setItem('heldCarts', JSON.stringify(validCarts));
        }
      } catch (e) {
        console.error('Error loading held carts:', e);
      }
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      fetchData();
    }
  }, [selectedSection, selectedCategory]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = getAuthToken(); // ✅ FIXED
      if (!token) throw new Error('No token found. Please log in.');
      
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const fetchData = async () => {
    try {
      const token = getAuthToken(); // ✅ FIXED
      if (!token) throw new Error('No token found. Please log in.');
      
      const params = { subBusiness: selectedSection };
      
      if (selectedCategory) {
        params.category = selectedCategory._id;
      }
      
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/pos`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setItems(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      alert('Failed to load items: ' + (err.response?.data?.message || err.message));
      if (err.response?.status === 401) {
        // ✅ FIXED: Clear correct token key and redirect to kiosk login
        localStorage.removeItem('kioskAuthToken');
        localStorage.removeItem('kioskUser');
        navigate('/kiosk/login');
      }
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = getAuthToken(); // ✅ FIXED
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 9999 } // Get all customers for POS kiosk
      });
      setCustomers(response.data.customers || response.data);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  const addToCart = (item) => {
    const existingItem = cart.find(i => i._id === item._id);
    if (existingItem) {
      updateCartQty(item._id, existingItem.cartQty + 1);
    } else {
      setCart([...cart, { ...item, cartQty: 1 }]);
    }
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item._id !== id));
  };

  const updateCartQty = (id, qty) => {
    if (qty <= 0) {
      removeFromCart(id);
    } else {
      setCart(cart.map(item => 
        item._id === id ? { ...item, cartQty: Math.min(qty, item.quantity || 999) } : item
      ));
    }
  };

  const handleDeleteCart = () => {
    if (window.confirm('Clear entire cart?')) {
      setCart([]);
      setCustomerDetails({ name: '', phone: '', email: '', dateOfBirth: '' });
      setDiscount(0);
    }
  };

  const handleHoldCart = () => {
    if (cart.length === 0) {
      alert('Cart is empty - nothing to hold');
      return;
    }

    const heldCart = {
      id: Date.now(),
      timestamp: Date.now(),
      cart: [...cart],
      customerDetails: { ...customerDetails },
      discount,
      applyVAT,
      section: selectedSection
    };

    const updatedHeldCarts = [...heldCarts, heldCart];
    setHeldCarts(updatedHeldCarts);
    sessionStorage.setItem('heldCarts', JSON.stringify(updatedHeldCarts));

    setCart([]);
    setCustomerDetails({ name: '', phone: '', email: '', dateOfBirth: '' });
    setDiscount(0);
    setApplyVAT(false);

    alert('✅ Cart held successfully!');
  };

  const handleRecallCart = (heldCartId) => {
  const heldCart = heldCarts.find(hc => hc.id === heldCartId);
  if (!heldCart) return;

  setCart(heldCart.cart);
  setCustomerDetails(heldCart.customerDetails);
  setDiscount(heldCart.discount);
  setApplyVAT(heldCart.applyVAT);
  if (heldCart.section) {
    setSelectedSection(heldCart.section);
  }

  const updatedHeldCarts = heldCarts.filter(hc => hc.id !== heldCartId);
  setHeldCarts(updatedHeldCarts);
  sessionStorage.setItem('heldCarts', JSON.stringify(updatedHeldCarts));

  setShowOrdersModal(false);

  alert('✅ Cart recalled successfully!');
};

  const handleDeleteHeldCart = (heldCartId) => {
    if (window.confirm('Delete this held cart?')) {
      const updatedHeldCarts = heldCarts.filter(hc => hc.id !== heldCartId);
      setHeldCarts(updatedHeldCarts);
      sessionStorage.setItem('heldCarts', JSON.stringify(updatedHeldCarts));
    }
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.cartQty), 0);
  };

  const calculateVAT = () => {
    return applyVAT ? calculateSubtotal() * VAT_RATE : 0;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const vat = calculateVAT();
    const discountAmount = (subtotal + vat) * (discount / 100);
    return subtotal + vat - discountAmount;
  };


  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }
    setShowPaymentModal(true);
  };

  // Helper function to extract first name from username
  const extractFirstName = (username) => {
    if (!username) return 'Staff';
    const parts = username.split('.');
    return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  };

  // Helper function to analyze cart and extract sub-businesses
  const analyzeCart = (cart) => {
    if (!cart || cart.length === 0) {
      return {
        subBusiness: [],
        section: 'General',
        category: 'Uncategorized'
      };
    }

    // Extract unique sub-businesses from cart items
    const subBusinesses = [...new Set(cart.map(item => item.subBusiness || item.section))];

    // Extract unique categories
    const categories = [...new Set(cart.map(item => {
      if (item.category?._id || item.category?.$oid) {
        return item.category.name || 'Uncategorized';
      }
      if (typeof item.category === 'string' && item.category !== '') {
        return item.category;
      }
      return 'Uncategorized';
    }))];

    return {
      subBusiness: subBusinesses,  // Always return array
      section: subBusinesses.length === 1 ? subBusinesses[0] : 'Multiple',
      category: categories.length === 1 ? categories[0] : 'Mixed'
    };
  };

  const confirmPayment = async () => {
    if (checkoutLoading) return;

    setCheckoutLoading(true);
    try {
      const token = getAuthToken();

      // ✅ FIX: Analyze cart to get correct sub-business(es)
      const { subBusiness, section, category } = analyzeCart(cart);

      // ✅ FIX: Ensure items include subBusiness field
      const transactionData = {
        items: cart.map(item => ({
          itemId: item._id,
          name: item.name,
          quantity: item.cartQty,
          price: item.price,
          type: item.type,
          subBusiness: item.subBusiness || item.section  // ✅ Include item's sub-business
        })),
        subtotal: calculateSubtotal(),
        vat: calculateVAT(),
        totalAmount: calculateTotal(),
        discount,
        applyVAT,
        paymentMethod,
        subBusiness: subBusiness,  // ✅ Array from cart analysis
        section: section,          // ✅ Single string or "Multiple"
        type: 'mixed',
        name: `Sale - ${new Date().toISOString()}`,
        category: category,        // ✅ From cart analysis
        status: 'completed',
        customer: customerDetails.name ? customerDetails : null,
        updateInventory: true,
        productItems: cart.filter(item => item.type === 'product').map(item => ({
          itemId: item._id,
          quantity: item.cartQty
        }))
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/pos-transactions`,
        transactionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowPaymentModal(false);

      // Show success notification for 1 second
      setShowSuccessNotification(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowSuccessNotification(false);

      // Print receipt
      const receiptData = {
        transactionId: response.data._id,
        cart,
        customerDetails,
        subtotal: calculateSubtotal(),
        vat: calculateVAT(),
        total: calculateTotal(),
        discount,
        section: section,  // ✅ Use analyzed section
        paymentMethod,
        applyVAT,
        timestamp: new Date(),
        cashier: extractFirstName(user?.username) || 'Staff'
      };

      const printer = new ReceiptPrinter();
      await printer.print(receiptData);

      // Reset
      setCart([]);
      setCustomerDetails({ name: '', phone: '', email: '', dateOfBirth: '' });
      setDiscount(0);
      setPaymentMethod('cash');
      setApplyVAT(false);

      // Refetch data
      if (selectedSection) {
        fetchData();
      }
      fetchCustomers();

    } catch (err) {
      console.error('Checkout error:', err);
      const errorMsg = err.response?.data?.message || err.message;
      const missingFields = err.response?.data?.missing;
      if (missingFields) {
        alert(`Checkout failed: Missing required fields - ${missingFields.join(', ')}`);
      } else {
        alert('Checkout failed: ' + errorMsg);
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleOrdersClick = async () => {
    setShowOrdersModal(true); // Open modal immediately
    try {
      const token = getAuthToken(); // ✅ FIXED
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/pos-transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(response.data || []);
    } catch (err) {
      setShowOrdersModal(false);
      alert('Failed to load orders: ' + err.message);
    }
  };

  const handleReprintTransaction = async (transaction) => {
    try {
      const receiptData = {
        transactionId: transaction._id,
        cart: transaction.items || [],
        customerDetails: transaction.customer || {},
        subtotal: transaction.subtotal || 0,
        vat: transaction.vat || 0,
        total: transaction.totalAmount || transaction.total || 0,
        discount: transaction.discount || 0,
        section: transaction.section,
        paymentMethod: transaction.paymentMethod || 'cash',
        applyVAT: transaction.applyVAT || false,
        timestamp: transaction.createdAt ? new Date(transaction.createdAt) : new Date(),
        cashier: 'System'
      };

      const printer = new ReceiptPrinter();
      await printer.print(receiptData);
    } catch (err) {
      alert('Reprint failed: ' + err.message);
    }
  };

  // ✅ Back to Sections - NO cart clearing (allows cross-sub-business shopping)
  const handleBackToSections = () => {
    setSelectedSection(null);
    setSelectedCategory(null);
    setItems([]);
  };

  // ✅ Home button - DOES clear cart with confirmation
  const handleHomeClick = () => {
    if (cart.length > 0) {
      const confirmed = window.confirm(
        '⚠️ Clear Cart?\n\n' +
        'Going home will clear your current cart.\n\n' +
        'Click OK to clear cart and go home.\n' +
        'Click Cancel to stay here.\n\n' +
        '(Tip: Use "Back to Sections" to add items from other sub-businesses without clearing cart)'
      );

      if (!confirmed) {
        return; // User cancelled, stay on current page
      }

      // User confirmed, clear cart
      setCart([]);
      setCustomerDetails({ name: '', phone: '', email: '', dateOfBirth: '' });
      setDiscount(0);
      setApplyVAT(false);
    }

    // Clear section and go home
    setSelectedSection(null);
    setSelectedCategory(null);
    setItems([]);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setItems([]);
  };

  const filteredItems = items.filter(item => {
  // First check if item belongs to selected category (if category is selected)
  if (selectedCategory) {
    const itemCategoryId = item.category?._id || item.category;
    if (itemCategoryId !== selectedCategory._id) {
      return false; // Skip items not in this category
    }
  }
  
  // Then apply search filter
  const matchesSearch = search 
    ? (item.name.toLowerCase().includes(search.toLowerCase()) || item.barcode?.includes(search))
    : true;
  
  return matchesSearch;
});

  const [mobileView, setMobileView] = useState('items'); // 'items' | 'cart'

  return (
    <div
      className="flex flex-col md:flex-row min-h-screen w-full"
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '14px',
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
      }}
    >
      {/* Items panel */}
      <div
        className={`w-full md:w-[70%] p-3 md:p-6 md:h-screen md:overflow-y-auto ${mobileView === 'cart' ? 'hidden md:block' : 'block'}`}
      >
        <POSHeader user={user} onLogout={logout} />

        {!selectedSection ? (
          <SectionSelector 
            selectedSection={selectedSection}
            onSelectSection={setSelectedSection}
          />
        ) : !selectedCategory ? (
          <CategoryGrid
            categories={categories}
            selectedSection={selectedSection}
            onSelectCategory={setSelectedCategory}
            onBack={handleBackToSections}
            items={items}
            onAddToCart={addToCart}
          />
        ) : (
          <ItemsGrid
            items={filteredItems}
            onAddToCart={addToCart}
            search={search}
            
            onSearchChange={setSearch}
            
            onBack={handleBackToCategories}
            selectedSection={selectedSection}
            selectedCategory={selectedCategory}
          />
        )}

        {/* Mobile: sticky "View Cart" button */}
        {cart.length > 0 && (
          <div
            className="md:hidden sticky bottom-0 mt-4 pt-2 pb-4"
            style={{ background: 'linear-gradient(to top, rgba(30,64,175,1) 60%, transparent)' }}
          >
            <button
              onClick={() => setMobileView('cart')}
              style={{
                width: '100%',
                padding: '1rem 1.25rem',
                border: 'none',
                borderRadius: '0.875rem',
                background: 'rgba(255,255,255,0.96)',
                color: '#1e40af',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
            >
              <span>View Cart ({cart.length} item{cart.length !== 1 ? 's' : ''})</span>
              <span style={{ color: '#2563eb', fontWeight: '800' }}>
                ₦{calculateTotal().toLocaleString('en-NG')}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Cart panel — fixed full-screen on mobile, sidebar on desktop */}
      <div
        className={`md:w-[30%] md:static md:flex md:flex-col md:h-screen md:sticky md:top-0 md:overflow-hidden ${
          mobileView === 'items' ? 'hidden md:flex' : 'fixed inset-0 z-[80] flex flex-col'
        }`}
      >
      <Cart
        cart={cart}
        customerDetails={customerDetails}
        discount={discount}
        applyVAT={applyVAT}
        heldCarts={heldCarts}
        calculateSubtotal={calculateSubtotal}
        calculateVAT={calculateVAT}
        calculateTotal={calculateTotal}
        onUpdateQty={updateCartQty}
        onRemoveItem={removeFromCart}
        onClearCart={handleDeleteCart}
        onHoldCart={handleHoldCart}
        onRecallCart={handleRecallCart}
        onDeleteHeldCart={handleDeleteHeldCart}
        onOpenCustomer={() => setShowCustomerModal(true)}
        onRemoveCustomer={() => setCustomerDetails({ name: '', phone: '', email: '', dateOfBirth: '' })}
        onSetDiscount={setDiscount}
        onSetApplyVAT={setApplyVAT}
        onCheckout={handleCheckout}
        onHomeClick={handleHomeClick}
        onCustomersClick={() => setShowCustomerModal(true)}
        onOrdersClick={handleOrdersClick}
        onBackToItems={() => setMobileView('items')}
      />
      </div>

      {showSuccessNotification && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#ffffff',
            padding: '2rem 3rem',
            borderRadius: '1rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>Payment Successful!</div>
          <div style={{ fontSize: '1rem', marginTop: '0.5rem', opacity: 0.9 }}>Preparing receipt...</div>
        </div>
      )}

      {showCustomerModal && (
        <CustomerModal
          customers={customers}
          customerDetails={customerDetails}
          onSave={(data) => {
            setCustomerDetails(data);
            setShowCustomerModal(false);
          }}
          onClose={() => setShowCustomerModal(false)}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          total={calculateTotal()}
          paymentMethod={paymentMethod}
          onSetPaymentMethod={setPaymentMethod}
          onConfirm={confirmPayment}
          onClose={() => setShowPaymentModal(false)}
          loading={checkoutLoading}
        />
      )}

      {showOrdersModal && (
  <OrdersModal
    orders={orders}
    heldCarts={heldCarts}
    onReprint={handleReprintTransaction}
    onRecallCart={handleRecallCart}
    onDeleteHeldCart={handleDeleteHeldCart}
    onClose={() => setShowOrdersModal(false)}
    loading={orders.length === 0}
  />
)}

      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translate(-50%, -60%);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%);
            }
          }
        `}
      </style>
    </div>
  );
}

export default POSSection;